import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Character, CharacterCreateRequest, Player, EquipRequest, UnequipRequest } from "@idle-rpg/shared";
import { apiCreateCharacter, apiDeleteCharacter, apiListCharacters, apiLogin, apiRegister, apiEquipItem, apiUnequipItem } from "../api/client";
import { useSessionSocket } from "../composables/useSessionSocket";

export const useSessionStore = defineStore("session", () => {
  const token = ref<string | null>(null);
  const player = ref<Player | null>(null);
  type CharacterClient = Character & { carry?: any };
  const characters = ref<CharacterClient[]>([]);
  const character = ref<CharacterClient | null>(null);
  const loading = ref(false);
  const backgroundLoading = ref(false);
  const error = ref<string | null>(null);

  const isAuthed = computed(() => !!token.value);

  async function register(email: string, password: string) {
    loading.value = true;
    error.value = null;
    const res = await apiRegister({ email, password });
    loading.value = false;
    if (res.ok) {
      token.value = res.data.token;
      player.value = res.data.player;
    } else {
      error.value = res.error.message;
    }
  }

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;
    const res = await apiLogin({ email, password });
    loading.value = false;
    if (res.ok) {
      token.value = res.data.token;
      player.value = res.data.player;
    } else {
      error.value = res.error.message;
    }
  }

  async function loadCharacters(options: { background?: boolean } = {}) {
    if (!token.value) return;
    const isBackground = options.background ?? false;
    if (isBackground) {
      if (backgroundLoading.value) return;
      backgroundLoading.value = true;
    } else {
      loading.value = true;
    }
    const res = await apiListCharacters(token.value);
    if (isBackground) {
      backgroundLoading.value = false;
    } else {
      loading.value = false;
    }
    if (res.ok) {
      characters.value = res.data.characters as CharacterClient[];
    } else {
      error.value = res.error.message;
    }
  }

  async function createCharacter(payload: CharacterCreateRequest) {
    if (!token.value) return;
    loading.value = true;
    const res = await apiCreateCharacter(token.value, payload);
    loading.value = false;
    if (res.ok) {
      characters.value.push(res.data.character as CharacterClient);
      character.value = res.data.character as CharacterClient;
    } else {
      error.value = res.error.message;
    }
  }

  function selectCharacter(id: string) {
    const found = characters.value.find((c) => c.id === id) || null;
    character.value = found;
  }

  async function refreshCharacter(options: { background?: boolean } = {}) {
    if (!token.value || !character.value) return;
    const currentId = character.value.id;
    await loadCharacters({ background: options.background });
    const updated = characters.value.find((c) => c.id === currentId);
    if (updated) {
      character.value = updated;
    }
  }

  async function refreshCharacterSnapshot() {
    if (!token.value || !character.value) return;
    const currentId = character.value.id;
    const sessionSocket = useSessionSocket();
    try {
      const snapshot = await sessionSocket.requestSnapshot("area", { include: ["core", "inventory", "equipment", "carry", "progress"] });
      if (!character.value || character.value.id !== currentId) return;
      const data = snapshot.data as any;

      if (data.inventory) character.value.inventory = data.inventory as Character["inventory"];
      if (data.equipment) character.value.equipment = data.equipment as Character["equipment"];
      if (data.gold !== undefined) character.value.gold = data.gold;
      if (data.carry !== undefined) character.value.carry = data.carry ?? null;
      if (data.hp !== undefined) character.value.hp = data.hp;
      if (data.mana !== undefined) character.value.mana = data.mana;
      if (data.skillProgress !== undefined) character.value.skillProgress = data.skillProgress;
      if (data.pendingLevelups !== undefined) character.value.pendingLevelups = data.pendingLevelups;
      if (data.apAvailable !== undefined) character.value.apAvailable = data.apAvailable;

      const idx = characters.value.findIndex((c) => c.id === currentId);
      if (idx !== -1) {
        const entry = characters.value[idx];
        if (data.inventory) entry.inventory = data.inventory as Character["inventory"];
        if (data.equipment) entry.equipment = data.equipment as Character["equipment"];
        if (data.gold !== undefined) entry.gold = data.gold;
        if (data.carry !== undefined) (entry as any).carry = data.carry ?? null;
        if (data.hp !== undefined) entry.hp = data.hp;
        if (data.mana !== undefined) entry.mana = data.mana;
        if (data.skillProgress !== undefined) entry.skillProgress = data.skillProgress;
        if (data.pendingLevelups !== undefined) entry.pendingLevelups = data.pendingLevelups;
        if (data.apAvailable !== undefined) entry.apAvailable = data.apAvailable;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to refresh character state";
    }
  }

  async function deleteCharacter(id: string) {
    if (!token.value) return;
    loading.value = true;
    error.value = null;
    const res = await apiDeleteCharacter(token.value, id);
    loading.value = false;
    if (res.ok) {
      characters.value = characters.value.filter((c) => c.id !== id);
      // If deleted character was selected, clear selection
      if (character.value?.id === id) {
        character.value = null;
      }
      return true;
    } else {
      error.value = res.error.message;
      return false;
    }
  }

  function logout() {
    token.value = null;
    player.value = null;
    characters.value = [];
    character.value = null;
    loading.value = false;
    backgroundLoading.value = false;
  }

  async function equipItem(itemId: string, slot: any, instanceId?: string | null) {
    if (!token.value || !character.value) return;
    loading.value = true;
    const reqId = crypto.randomUUID();
    const res = await apiEquipItem(token.value, character.value.id, {
      itemId,
      instanceId,
      slot,
      clientRequestId: reqId
    });
    loading.value = false;
    if (res.ok) {
      await refreshCharacterSnapshot();
      return true;
    } else {
      error.value = res.error.message;
      return false;
    }
  }

  async function unequipItem(slot: any) {
    if (!token.value || !character.value) return;
    loading.value = true;
    const reqId = crypto.randomUUID();
    const res = await apiUnequipItem(token.value, character.value.id, {
      slot,
      clientRequestId: reqId
    });
    loading.value = false;
    if (res.ok) {
      await refreshCharacterSnapshot();
      return true;
    } else {
      error.value = res.error.message;
      return false;
    }
  }

  return {
    token,
    player,
    characters,
    character,
    loading,
    backgroundLoading,
    error,
    isAuthed,
    register,
    login,
    loadCharacters,
    createCharacter,
    selectCharacter,
    refreshCharacter,
    refreshCharacterSnapshot,
    deleteCharacter,
    logout,
    equipItem,
    unequipItem
  };
});
